import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputBase,
  IconButton,
  Button,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import createInstance from "../../api/axiosinstance";
import Toast from "../../utils/toast";
import Loader from "../Loader";
import ScheduleInterview from "./ScheduleInterview";
import ViewModal from "./ViewModal";

const InterviewsTable = () => {
  const [tabValue, setTabValue] = useState("scheduled");
  const [scheduledInterviews, setScheduledInterviews] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [completedInterviews, setCompletedInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [cancelledInterviews, setCancelledInterviews] = useState([]);
  const [loading, setLoading] = useState(true); // optional
  const [error, setError] = useState(null); // optional
  const { user } = useSelector((state) => state.auth);
  const organizationId = user?.organizationId;
  const hrId = user?.orgUserId;
  const aiDishaApi = createInstance("DISHA");

  const fetchScheduledInterviews = async () => {
    try {
      setIsLoading(true);
      const response = await aiDishaApi.get(
        `/get_Scheduled_interviews_detail?organization_id=${organizationId}&Hr_id=${hrId}`
      );
      console.log("schedule interview values", response.data);
      setScheduledInterviews(
        Array.isArray(response.data) ? [...response.data].reverse() : []
      );
    } catch (error) {
      console.error("Error fetching scheduled interviews:", error);
      Toast.error("Failed to fetch scheduled interviews. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUpcomingInterviews = async () => {
    try {
      setIsLoading(true);
      const response = await aiDishaApi.get(
        `/upcoming_interviews?organization_id=${organizationId}&Hr_id=${hrId}`
      );
      setUpcomingInterviews(
        Array.isArray(response.data.upcoming_interviews)
          ? [...response.data.upcoming_interviews].reverse()
          : []
      );
    } catch (error) {
      console.error("Error fetching upcoming interviews:", error);
      Toast.error("Failed to fetch upcoming interviews. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompletedInterviews = async () => {
    try {
      setIsLoading(true);
      const response = await aiDishaApi.get(
        `/get_all_completed_interviews?organization_id=${organizationId}`
      );
      console.log("completed interviews", response.data);
      setCompletedInterviews(
        Array.isArray(response.data) ? [...response.data].reverse() : []
      );
    } catch (error) {
      console.error("Error fetching completed interviews:", error);
      Toast.error("Failed to fetch completed interviews. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCancelledInterviews = async () => {
      try {
        const response = await aiDishaApi.get(
          `/cancelled_interviews?organization_id=${organizationId}`
        );
        console.log("Fetched cancelled interviews:", response.data);
        setCancelledInterviews(response.data); // or response.data.data if wrapped
        setLoading(false);
      } catch (err) {
        console.error("Error fetching cancelled interviews:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCancelledInterviews();
  }, [organizationId]);

  const handleCancelInterview = async (interview) => {
    if (!window.confirm("Are you sure you want to cancel this interview?"))
      return;
    try {
      const response = await aiDishaApi.patch(
        `/interview_soft_delete?interview_id=${interview.interview_id}&status=InActive`
      );
      if (response.data.message === "Interview Status Updated!") {
        Toast.success("Interview cancelled successfully!");
        if (tabValue === "scheduled") fetchScheduledInterviews();
        else if (tabValue === "upcoming") fetchUpcomingInterviews();
      } else {
        Toast.error("Failed to cancel interview. Please try again.");
      }
    } catch (error) {
      console.error("Error cancelling interview:", error);
      Toast.error(
        "Failed to cancel interview. Please verify interview details."
      );
    }
  };

  useEffect(() => {
    if (organizationId && hrId && tabValue === "scheduled") {
      fetchScheduledInterviews();
    } else if (organizationId && hrId && tabValue === "upcoming") {
      fetchUpcomingInterviews();
    } else if (organizationId && tabValue === "completed") {
      fetchCompletedInterviews();
    }
  }, [tabValue, organizationId, hrId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
    setSearchTerm("");
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewModalOpen = (interview) => {
    const normalized = normalizeInterview(interview);
    setSelectedInterview(normalized);
    setIsViewModalOpen(true);
  };

  const handleEditModalOpen = (interview) => {
    setSelectedInterview(interview);
    setIsEditModalOpen(true);
  };

  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setSelectedInterview(null);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedInterview(null);
  };

  const formatDateTime = (interview) => {
    let dateTimeField;

    if (tabValue === "completed") {
      dateTimeField = interview.date_and_time;
    } else {
      dateTimeField = interview.interview_date_and_time;
    }

    if (!dateTimeField) return "N/A";

    const date = new Date(dateTimeField);

    if (isNaN(date.getTime())) return "Invalid Date";

    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const header = [
    {
      interviewId: "Interview ID",
      candidateName: "Candidate Name",
      appliedPosition: "Applied Position",
      interviewDate: "Interview Date",
      status: "Status",
      interviewerAcceptance: "Interviewer Acceptance",
      action: "Action",
      round: "Round",
      round_id: "Round ID",
      hr_decision: "HR Decision",
    },
  ];

  const filteredScheduledInterviews = scheduledInterviews.filter((row) =>
    row.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUpcomingInterviews = upcomingInterviews.filter((row) =>
    row.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCompletedInterviews = completedInterviews.filter((row) =>
    row.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredcancelledInterviews = cancelledInterviews.filter((row) =>
    row.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  console.log("tabValue: ", tabValue);

  const normalizeInterview = (interview) => {
    return {
      ...interview,

      // normalize datetime
      interview_date_and_time:
        interview.interview_date_and_time || interview.date_and_time || null,

      // normalize meeting details
      meeting_id: interview.meeting_id || interview.meetingId || "N/A",

      meeting_link: interview.meeting_link || interview.meetingLink || "N/A",

      interviewer_name:
        interview.interviewer_name || interview.interviewer || "N/A",

      interviewer_acceptance:
        interview.Interviewer_acceptance ||
        interview.interviewer_acceptance ||
        "N/A",
    };
  };

  const renderTable = (interviews) => (
    <Box xs={1} sx={{ backgroundColor: "#33333" }}>
      <Grid container spacing={1} sx={{ justifyContent: "center" }}>
        <Grid item xs={10}>
          <TableContainer
            component={Paper}
            sx={{
              marginTop: "25px",
              borderRadius: "10px",
            }}
          >
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#033538" }}>
                {header.map((head) => (
                  <TableRow key={head.interviewId}>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        borderLeft: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {head.interviewId}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {head.candidateName}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {head.appliedPosition}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {head.interviewDate}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {tabValue === "completed"
                        ? head.round_id
                        : tabValue === "scheduled"
                        ? head.round
                        : head.status}
                      {/* // {head.status} */}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {tabValue === "completed"
                        ? head.hr_decision
                        : head.interviewerAcceptance}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "white",
                        borderBottom: "1px solid #006A71",
                        fontWeight: "bold",
                        fontFamily: "Poppins",
                      }}
                    >
                      {head.action}
                    </TableCell>
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {interviews
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((interview) => (
                    <TableRow
                      key={interview.interview_id}
                      sx={{ backgroundColor: "#006A71" }}
                    >
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                        }}
                      >
                        {interview.interview_id}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                        }}
                      >
                        {interview.candidate_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                        }}
                      >
                        {interview.job_title}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                        }}
                      >
                        {formatDateTime(interview)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                        }}
                      >
                        {tabValue === "Cancel"
                          ? interview.status
                          : tabValue === "completed"
                          ? interview.round_id
                          : interview.round}
                        {/* {tabValue === "completed"
                          ? interview.Interview_status
                          : interview.round} */}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                        }}
                      >
                        {tabValue === "completed"
                          ? interview.Hr_decision
                          : interview.Interviewer_acceptance}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "white",
                          border: "1px solid #033538",
                          fontFamily: "Poppins",
                          padding: "4px",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewModalOpen(interview)}
                          sx={{
                            backgroundColor: "#0288d1",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "#0277bd",
                            },
                            fontFamily: "Poppins",
                            textTransform: "none",
                            mr: 1,
                            padding: "4px 8px",
                          }}
                        >
                          View
                        </Button>
                        {(tabValue === "scheduled" ||
                          tabValue === "upcoming") && (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditModalOpen(interview)}
                              sx={{
                                backgroundColor: "#033538",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#02484b",
                                },
                                fontFamily: "Poppins",
                                textTransform: "none",
                                mr: 1,
                                padding: "4px 8px",
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleCancelInterview(interview)}
                              sx={{
                                backgroundColor: "#d32f2f",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#b71c1c",
                                },
                                fontFamily: "Poppins",
                                textTransform: "none",
                                padding: "4px 8px",
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 15]}
              component="div"
              count={interviews.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                backgroundColor: "#033538",
                color: "white",
                fontFamily: "Poppins",
                "& .MuiTablePagination-selectLabel": {
                  color: "white",
                },
                "& .MuiTablePagination-displayedRows": {
                  color: "white",
                },
                "& .MuiSelect-icon": {
                  color: "white",
                },
              }}
            />
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          backgroundColor: "#33333",
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#33333" }}>
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: "28px",
          color: "#006A71",
          mb: 2,
        }}
      >
        Interviews
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "auto",
          alignItems: "center",
          borderRadius: "10px",
          padding: "1% 1%",
        }}
      >
        <Box
          gridColumn="span 6"
          sx={{
            marginTop: "auto",
            textAlign: "left",
            marginBottom: "2%",
            marginLeft: "8%",
          }}
        >
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "75%",
              borderRadius: "4px",
              backgroundColor: "#033538",
              border: "1px solid #006A71",
            }}
          >
            <IconButton
              type="button"
              sx={{ p: "8px", color: "white" }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{
                ml: 1,
                flex: 1,
                fontFamily: "Poppins",
                color: "white",
                "&::placeholder": {
                  color: "#A7A7A7",
                },
              }}
              placeholder="Search by Applied Position"
              inputProps={{ "aria-label": "search by applied position" }}
              value={searchTerm}
              onChange={handleSearch}
            />
          </Paper>
        </Box>
      </Box>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          mb: 2,
          "& .MuiTab-root": {
            color: "#A7A7A7",
            fontFamily: "Poppins",
            "&.Mui-selected": {
              color: "#006A71",
            },
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#006A71",
          },
        }}
      >
        <Tab label="Scheduled Interviews" value="scheduled" />
        <Tab label="Upcoming Interviews" value="upcoming" />
        <Tab label="Completed Interviews" value="completed" />
        <Tab label="Cancelled Interviews" value="Cancel" />
      </Tabs>
      {tabValue === "scheduled" && renderTable(filteredScheduledInterviews)}
      {tabValue === "upcoming" && renderTable(filteredUpcomingInterviews)}
      {tabValue === "completed" && renderTable(filteredCompletedInterviews)}
      {tabValue === "Cancel" && renderTable(filteredcancelledInterviews)}
      <ViewModal
        isOpen={isViewModalOpen}
        onClose={handleViewModalClose}
        title="View Interview"
        candidateId={selectedInterview?.candidate_id}
        interview={selectedInterview}
        isReadOnly={true}
      />
      <ViewModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        title="Edit Interview"
        candidateId={selectedInterview?.candidate_id}
        interview={selectedInterview}
        onUpdate={() => {
          fetchScheduledInterviews();
          fetchUpcomingInterviews();
        }}
        isReadOnly={false}
      />
    </Box>
  );
};

export default InterviewsTable;
